import {APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {DefaultTemplateComponent} from './templates/default/default.component';
import {DatePipe, registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import {PortalMessageBroker} from './portal/portal-message-broker';
import {PortalTokenService} from './portal/portal-token.service';
import {
  AuthInterceptor as OidcAuthInterceptor,
  AuthModule,
  OidcSecurityService,
  OpenIdConfiguration,
  StsConfigHttpLoader,
  StsConfigLoader
} from 'angular-auth-oidc-client';
import {
  ApiModule as ZahlungApiModule,
  Configuration as ZahlungConfiguration,
  InhaberService
} from './openapi/zahlung-openapi';
import {ApiModule as BelegApiModule, Configuration as BelegConfiguration} from './openapi/beleg-openapi';
import {httpInterceptorProviders} from './http-interceptors';
import {environment} from '../environments/environment';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {StoreModule} from '@ngrx/store';
import {StoreDevtoolsModule} from '@ngrx/store-devtools';
import {EffectsModule} from '@ngrx/effects';
import {RouterState, StoreRouterConnectingModule} from '@ngrx/router-store';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule} from '@angular/material/core';
import {NoopScrollStrategy} from '@angular/cdk/overlay';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatMultiSortModule} from 'ngx-mat-multi-sort';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {MY_FORMATS} from './modules/edit-form/edit-form.module';
import {WizardTemplateComponent} from './templates/wizard/wizard.component';
import {PaymentBrowserModule} from './templates/wizard/payment-browser/payment-browser.module';
import {mergeMap, tap} from 'rxjs';
import {ConfigModule} from './config/config.module';
import {filter, map, take} from 'rxjs/operators';
import {HTTP_INTERCEPTORS} from '@angular/common/http';
import {AuthInterceptor} from './auth/auth.interceptor';
import {ConfigService} from './config/service/config.service';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import * as Sentry from '@sentry/angular-ivy';
import jwtDecode, {JwtPayload} from 'jwt-decode';
import {ContextInfoService, WellKnownJustFarmingProperties} from './portal/context-info.service';
import {metaReducers, reducers} from './store/reducers/reducers';
import {SentryEffects} from './store/effects/sentry.effects';
import {
  AppSkeletonModule,
  BetriebsauswahlModule,
  ButtonModule,
  CONTEXT_INFO,
  ErrorPagesModule,
  INHABER_SERVICE,
  NavigationModule,
  NGX_LOGGER,
  provideDialogs,
  provideTheme,
} from '@adnova/jf-ng-components';
import {ZahlungSelectors} from './store/selectors/zahlung.selectors';
import {ZahlungActions} from './store/actions/zahlung.actions';
import {MemoizedSelector} from '@ngrx/store';
import {BetriebsauswahlPageComponent} from './modules/pages/betriebsauswahl-page/betriebsauswahl-page.component';
import {MatDialogModule} from '@angular/material/dialog';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {BelegEffects} from './store/effects/beleg.effects';
import {BelegSelectors} from './store/selectors/beleg.selectors';
import {BelegActions} from './store/actions/beleg.actions';
import {TransactionSelectors} from './store/selectors/transaction.selectors';
import {TransactionActions} from './store/actions/transaction.actions';
import {MAT_TOOLTIP_DEFAULT_OPTIONS} from '@angular/material/tooltip';


registerLocaleData(localeDe, 'de-DE', localeDeExtra);

@NgModule({
  declarations: [
    AppComponent,
    DefaultTemplateComponent,
    WizardTemplateComponent,
    BetriebsauswahlPageComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    AppRoutingModule,
    MatSnackBarModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatMultiSortModule,
    NavigationModule,
    PaymentBrowserModule,
    MatDialogModule,
    AppSkeletonModule,
    BetriebsauswahlModule,
    // Config
    ConfigModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.INFO, // default logging level
      enableSourceMaps: !environment.production,
    }),
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: (configService: ConfigService) => {
          const configObservable = configService.loaded$
            .pipe(
              take(1),
              map(config => {
                const authConfig: OpenIdConfiguration = {
                  // loaded
                  authority: config.auth.authority,
                  clientId: config.auth.clientId,
                  scope: config.auth.scope,
                  logLevel: config.auth.logLevel,
                  renewTimeBeforeTokenExpiresInSeconds: config.auth.renewTimeBeforeTokenExpiresInSeconds,
                  // predefined
                  redirectUrl: window.location.origin,
                  postLogoutRedirectUri: window.location.origin,
                  responseType: 'code',
                  silentRenew: true,
                  triggerAuthorizationResultEvent: true,
                  ignoreNonceAfterRefresh: true,
                  useRefreshToken: true,
                  // INFO: Bei anpassungen auch AuthInterceptor.matchesSecureRoots() anpassen
                  secureRoutes: [
                    config.beleg.apiBaseUrl,
                    config.zahlung.apiBaseUrl,
                  ],
                };
                return authConfig;
              }),
            );
          return new StsConfigHttpLoader(configObservable);
        },
        deps: [ConfigService],
      },
    }),
    ZahlungApiModule.forRoot(() => new ZahlungConfiguration()),
    BelegApiModule.forRoot(() => new BelegConfiguration()),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictActionSerializability: true,
        strictStateSerializability: true,
      }
    }),
    StoreDevtoolsModule.instrument({
      name: 'Zahlung App',
      maxAge: 25,
      logOnly: environment.production,
    }),
    EffectsModule.forRoot([
      SentryEffects,
      BelegEffects,
    ]),
    StoreRouterConnectingModule.forRoot({
      stateKey: 'router',
      routerState: RouterState.Minimal,
    }),
    ErrorPagesModule,
    ButtonModule,
  ],
  exports: [
    AuthModule,
  ],
  providers: [
    OidcAuthInterceptor,
    {
      // INFO: Interceptor um automatisch da OAuth Token zu setzen
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      // INFO: Initializer das Logging
      provide: APP_INITIALIZER,
      useFactory: (configService: ConfigService, logger: NGXLogger) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              logger.updateConfig({
                ...logger.getConfigSnapshot(),
                level: value.logging.level
              });
            }),
          );
      },
      deps: [ConfigService, NGXLogger],
      multi: true,
    },
    {
      // INFO: Initializer für Sentry Integration
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        contextInfoService: ContextInfoService,
        logger: NGXLogger,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(config => {
              const sentry = config.sentry;
              const info = environment.info;
              if (sentry) {
                Sentry.init({
                  enabled: sentry.enabled,
                  debug: sentry.debug,
                  environment: config.environment,
                  release: info.commit,
                  dsn: sentry.dsn,
                  sampleRate: sentry.sampleRate,
                  autoSessionTracking: false,
                  enableTracing: false,
                  // maxBreadcrumbs nur erhöhen, wenn niemals sensiblen Daten mitgeschrieben werden
                  // https://docs.sentry.io/platforms/javascript/guides/angular/configuration/integrations/default/#breadcrumbs
                  maxBreadcrumbs: 0,
                  initialScope: {
                    extra: {
                      'info': info,
                    },
                  },
                  beforeSend: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)) {
                      return event;
                    }
                    logger.debug('Error reporting is disabled, dropping event');
                    return null;
                  },
                  beforeSendTransaction: (event) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return event;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping transaction event');
                    return null;
                  },
                  beforeBreadcrumb: (breadcrumb) => {
                    let contextInfo = contextInfoService?.contextInfo;
                    if (
                      contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS)
                      && contextInfo?.properties.get(WellKnownJustFarmingProperties.ERROR_REPORTING)
                    ) {
                      return breadcrumb;
                    }
                    logger.debug('Error reporting or analytics are disabled, dropping breadcrumb');
                    return null;
                  },
                });
              }
            }),
          );
      },
      deps: [
        ConfigService,
        ContextInfoService,
        NGXLogger,
      ],
      multi: true,
    },
    {
      // INFO: Angular Sentry ErrorHandler
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      // INFO: Initializer der die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        belegConfiguration: BelegConfiguration,
        zahlungConfiguration: ZahlungConfiguration,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            tap(value => {
              belegConfiguration.basePath = value.beleg.apiBaseUrl;
              zahlungConfiguration.basePath = value.zahlung.apiBaseUrl;
            }),
          );
      },
      deps: [ConfigService, BelegConfiguration, ZahlungConfiguration],
      multi: true,
    },
    {
      // INFO: Initializer für Portal integration die Base-URL setzt
      provide: APP_INITIALIZER,
      useFactory: (
        configService: ConfigService,
        belegConfiguration: BelegConfiguration,
        portalMessageBroker: PortalMessageBroker,
        portalTokenService: PortalTokenService,
        oidcSecurityService: OidcSecurityService,
      ) => () => {
        return configService.loaded$
          .pipe(
            take(1),
            mergeMap(config => {
              return fromPromise(portalMessageBroker.init(config.portal.targetOrigin))
                .pipe(map(() => config));
            }),
            mergeMap(config => {
              return fromPromise(portalTokenService.init(config.auth.renewTimeBeforeTokenExpiresInSeconds))
                .pipe(map(() => config));
            }),
            tap(() => {
              const runningEmbedded = portalMessageBroker.isRunningInPortal();
              Sentry.setTag('runningEmbedded', runningEmbedded);

              if (runningEmbedded) {
                const decoded = jwtDecode<JwtPayload>(portalTokenService.getAdnovaToken());
                const token = decoded as any;

                const email = token.email;
                const username = token.preferred_username;
                const name = token.name;

                Sentry.withScope(scope => {
                  let user = scope.getUser();
                  if (!user) {
                    user = {};
                  }
                  user.id = decoded.sub;
                  user.email = email;
                  user.username = username;
                  user['name'] = name;

                  scope.setUser(user);
                });
              } else {
                oidcSecurityService.getAccessToken()
                  .pipe(
                    filter(value => {
                      if (value) {
                        return true;
                      }
                      return false;
                    }),
                  )
                  .subscribe(accessToken => {
                    const decoded = jwtDecode<JwtPayload>(accessToken);
                    const token = decoded as any;

                    const email = token.email;
                    const username = token.preferred_username;
                    const name = token.name;

                    Sentry.withScope(scope => {
                      let user = scope.getUser();
                      if (!user) {
                        user = {};
                      }
                      user.id = decoded.sub;
                      user.email = email;
                      user.username = username;
                      user['name'] = name;

                      scope.setUser(user);
                    });
                  });
              }
            }),
          );
      },
      deps: [ConfigService, BelegConfiguration, PortalMessageBroker, PortalTokenService, OidcSecurityService],
      multi: true,
    },
    {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
    {provide: MAT_DATE_FORMATS, useValue: MY_FORMATS},
    DatePipe,
    MatDatepickerModule,
    NoopScrollStrategy,
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        disableTooltipInteractivity: true,
        showDelay: 600,
        hideDelay: 150,
      },
    },
    provideTheme(),
    {
      // INFO: Zugriff auf den ContextInfoService gewähren
      provide: CONTEXT_INFO,
      useClass: ContextInfoService,
    },
    {
      // INFO: Zugriff auf den InhaberService gewähren
      provide: INHABER_SERVICE,
      useClass: InhaberService,
    },
    {
      provide: NGX_LOGGER,
      useClass: NGXLogger,
    },
    httpInterceptorProviders,
    provideDialogs([
      {
        key: 'zahlungDeleteDialog',
        isOpenSelector: ZahlungSelectors.isDeleteDialogOverlayOpen as MemoizedSelector<object, boolean>,
        closeAction: ZahlungActions.closeZahlungDeleteDialog,
        loader: () => import('./modules/delete-dialog/overlay/delete-dialog.component').then(c => c.DeleteDialogComponent),
        config: {
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'belegCancelledDialog',
        isOpenSelector: BelegSelectors.isBelegCancelledDialogOverlayOpen as MemoizedSelector<object, boolean>,
        closeAction: BelegActions.closeBelegCancelledDialog,
        loader: () => import('./modules/beleg-cancelled-dialog/beleg-cancelled-dialog.component').then(c => c.BelegCancelledDialogComponent),
        config: {
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'sessionExpiredDialog',
        isOpenSelector: TransactionSelectors.isSessionExpiredDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: TransactionActions.closeSessionExpiredDialog,
        loader: () => import('./modules/transaction/session-expired-dialog/session-expired-dialog.component').then(c => c.SessionExpiredDialogComponent),
        config: {
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'zahlungDeletedDialog',
        isOpenSelector: ZahlungSelectors.isZahlungDeletedDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: ZahlungActions.closeZahlungDeletedDialog,
        loader: () => import('./modules/deleted-dialog/deleted-dialog.component').then(c => c.DeletedDialogComponent),
        config: {
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'failureMessageDialog',
        isOpenSelector: TransactionSelectors.isFailureMessageDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: TransactionActions.closeFailureMessageDialog,
        loader: () => import('./modules/transaction/failure-message-dialog/failure-message-dialog.component').then(c => c.FailureMessageDialogComponent),
        config: {
          disableClose: true,
          autoFocus: false,
          showCloseButton: false,
        },
      },
      {
        key: 'belegFilterDialog',
        isOpenSelector: BelegSelectors.isBelegFilterDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: BelegActions.closeBelegFilterDialog,
        loader: () => import('./modules/beleg-filter-dialog/beleg-filter-dialog.component').then(c => c.BelegFilterDialogComponent),
        config: {
          disableClose: false,
          minWidth: '850px',
          width: '1200px',
          maxWidth: '1200px',
          height: '910px',
          autoFocus: false,
          showCloseButton: true,
        },
      },
      {
        key: 'pdfViewerDialog',
        isOpenSelector: BelegSelectors.isPdfViewerDialogOpen as MemoizedSelector<object, boolean>,
        closeAction: BelegActions.closePdfViewerDialog,
        loader: () => import('./modules/pdf-viewer-dialog/pdf-viewer-dialog.component').then(c => c.PdfViewerDialogComponent),
        config: {
          width: '95%',
          height: '95%',
          maxWidth: 'unset',
          autoFocus: false,
          showCloseButton: true,
        },
      },
    ]),
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
